import React, { forwardRef, useImperativeHandle, isValidElement } from 'react';
import { useDropzone } from 'react-dropzone';
import type { Types } from '@fiverr-private/theme';
import { Input } from '@fiverr-private/inputs';
import { renderChildren } from '@fiverr-private/ui_utils';
import { Center, Container } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { I18n } from '@fiverr-private/i18n-react';
import type { DropzoneProps } from './types';
import UploadingBody from './UploadingBody';
import IdleBody from './IdleBody';
import Disclaimer from './Disclaimer';

const Dropzone = forwardRef<HTMLInputElement, DropzoneProps>(
  (
    {
      showDisclaimer = true,
      isLoading,
      maxFiles,
      isError,
      onBlur,
      onFocus,
      name,
      children,
      minHeight,
      disabled,
      accept,
      maxSize,
      progressPercent,
      multiple,
      variant = 'default',
      goExperimental,
      theme = 'light',
      ...props
    }: DropzoneProps,
    ref
  ) => {
    const isDark = goExperimental || theme === 'dark';
    const { getInputProps, getRootProps, isDragActive, inputRef, acceptedFiles } = useDropzone({
      ...props,
      disabled,
      maxFiles,
      accept,
      maxSize,
      multiple,
    });

    const stateColor: Types.BorderColor = isDragActive ? 'grey_1200' : 'border';
    const bodyByState = isLoading ? (
      <UploadingBody acceptedFiles={acceptedFiles} progressPercent={progressPercent} variant={variant} theme={theme} />
    ) : (
      <IdleBody multiple={multiple} disabled={disabled} variant={variant} theme={theme} />
    );

    useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => inputRef.current);

    const getBackgroundColor = (): Types.BoxType['backgroundColor'] => {
      if (isDark) {
        return isDragActive ? 'black_20' : 'black_10';
      }

      return isDragActive || disabled
        ? 'grey_100'
        : { default: 'white', hover: isLoading || disabled ? undefined : 'grey_100' };
    };

    const getBorderColor = (): Types.BoxType['borderColor'] => {
      if (isDark) {
        return 'black_50';
      }

      return isError
        ? 'validationError'
        : {
            default: stateColor,
            hover: isLoading || disabled ? undefined : ('grey_1200' as Types.BorderColor),
          };
    };

    return (
      <Center
        cursor={disabled ? 'notAllowed' : 'pointer'}
        backgroundColor={getBackgroundColor()}
        minHeight={minHeight}
        borderStyle="dashed"
        borderColor={getBorderColor()}
        outlineColor={{ focusVisible: 'grey_1200' }}
        outlineWidth={{ focusVisible: 'md' }}
        outlineStyle={{ focusVisible: 'solid' }}
        borderRadius="lg"
        borderWidth="sm"
        paddingTop="4"
        paddingBottom={(showDisclaimer || isLoading) && variant === 'default' ? '4' : undefined}
        paddingX="6"
        outlineOffset={{ focusVisible: 'sm' }}
        {...getRootProps({
          onBlur,
          onFocus,
        })}
      >
        <Input {...getInputProps({ name })} />
        {isValidElement(children) ? (
          renderChildren(children, { isDragActive })
        ) : (
          <>
            <Container paddingTop="2" paddingBottom="6">
              {bodyByState}
            </Container>
            {variant === 'default' &&
              (isLoading ? (
                <Typography textAlign="center" size="b_sm" color={isDark ? 'grey_300' : 'bodySecondary'}>
                  <I18n k="dropzone.uploading_body.upload_in_progress" params={{ count: acceptedFiles.length }} />
                </Typography>
              ) : (
                <Disclaimer
                  showDisclaimer={showDisclaimer}
                  maxSize={maxSize}
                  accept={accept}
                  maxFiles={maxFiles}
                  theme={theme}
                />
              ))}
          </>
        )}
      </Center>
    );
  }
);

Dropzone.displayName = 'Dropzone';

export default Dropzone;
